import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Zap, 
  Settings, 
  Wrench, 
  TrendingUp, 
  FileText, 
  ShieldCheck,
  Mail,
  Phone,
  MapPin,
  Clock,
  ChevronRight,
  CheckCircle2,
  Instagram,
  Linkedin
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedOptionals, setExpandedOptionals] = useState({});
  const [expandedFeaturedService, setExpandedFeaturedService] = useState(false);
  
  // Refs para armazenar as posições dos botões "Saiba Mais"
  const optionalRefs = React.useRef({});
  const featuredServiceRef = React.useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success("Mensagem enviada com sucesso!", {
          description: "Entraremos em contato em breve.",
        });
        setFormData({ name: '', email: '', company: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("Erro ao enviar mensagem", {
        description: "Por favor, tente novamente.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleOptionalDetails = (index) => {
    const isCurrentlyExpanded = expandedOptionals[index];
    
    // Se está expandido e vai colapsar, rola de volta para a posição do botão
    if (isCurrentlyExpanded && optionalRefs.current[index]) {
      setTimeout(() => {
        optionalRefs.current[index].scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
          inline: 'nearest'
        });
      }, 150);
    }
    
    setExpandedOptionals(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleFeaturedServiceDetails = () => {
    const isCurrentlyExpanded = expandedFeaturedService;
    
    // Se está expandido e vai colapsar, rola de volta para a posição do botão
    if (isCurrentlyExpanded && featuredServiceRef.current) {
      setTimeout(() => {
        featuredServiceRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }, 150);
    }
    
    setExpandedFeaturedService(prev => !prev);
  };

  const services = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Projetos em Elétrica e Automação",
      description: "Desenvolvemos projetos sob medida com integração total entre elétrica, mecânica e automação industrial. Garantimos precisão, eficiência e resultados reais para sua indústria.",
      keywords: "projetos elétricos, automação industrial, engenharia elétrica, sistemas automatizados"
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Montagem de Painéis Elétricos",
      description: "Painéis elétricos industriais montados com rigor técnico, qualidade e atenção aos detalhes. Cada componente é escolhido para garantir desempenho e segurança máxima conforme normas técnicas.",
      keywords: "painéis elétricos industriais, montagem de painéis, quadros elétricos"
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Manutenção Corretiva Emergencial",
      description: "Manutenção industrial 24h com diagnóstico rápido, resposta imediata e soluções seguras para minimizar paradas e prejuízos produtivos da sua linha de produção.",
      keywords: "manutenção industrial emergencial, assistência 24h, manutenção corretiva"
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Plano Diretor em Automação",
      description: "Estratégia completa para evolução tecnológica da sua planta industrial, com planejamento, integração de sistemas e documentação padronizada. Indústria 4.0.",
      keywords: "plano diretor automação, indústria 4.0, planejamento industrial, transformação digital"
    },
    {
      icon: <ShieldCheck className="w-8 h-8" />,
      title: "Adequação às Normas NR-10 e NR-12",
      description: "Adequação e regularização industrial conforme normas NR-10 e NR-12. Garantimos total conformidade com as normas de segurança e operação de máquinas e instalações elétricas.",
      keywords: "adequação NR-10, adequação NR-12, segurança do trabalho, normas regulamentadoras"
    }
  ];

  const featuredService = {
    title: "Desenvolvimento de Máquinas e Equipamentos",
    description: "Projetamos e fabricamos máquinas industriais personalizadas sob medida, otimizando produtividade, segurança e eficiência operacional. Cada equipamento é desenvolvido com foco em performance, durabilidade e integração perfeita aos processos da sua indústria.",
    detailedDescription: "Além de soluções convencionais, nossa empresa é especializada no desenvolvimento de máquinas especiais, projetadas a partir de necessidades específicas que não encontram soluções disponíveis no mercado. Cada projeto é conduzido com base em engenharia aplicada, análise detalhada de processo, estudos de viabilidade técnica e conformidade com normas industriais vigentes. As máquinas são concebidas para atender operações singulares, integrar-se a sistemas existentes e resolver gargalos produtivos por meio de soluções exclusivas, robustas e tecnicamente validadas.",
    image: "https://images.unsplash.com/photo-1717386255773-1e3037c81788",
    keywords: "máquinas industriais customizadas, desenvolvimento de equipamentos, engenharia mecânica, fabricação sob medida"
  };

  const machines = {
    title: "Strechadeira Envolvedora de Filme Stretch",
    description: "Máquina envolvedora de stretch para cargas paletizadas com filme stretch. Ideal para armazéns, centros de distribuição e indústrias que buscam agilidade, segurança e economia no processo de paletização e embalagem de cargas.",
    image: "https://customer-assets.emergentagent.com/job_automation-hub-52/artifacts/lugk58me_NORMAL_LATERAL_ESQUERDA.jpeg",
    keywords: "strechadeira industrial, envolvedora de paletes, máquina stretch wrapper, paletizadora automática, filme stretch",
    optionals: [
      {
        title: "Cabeçote Automático de Pré-Estiramento",
        description: "O cabeçote de pré-estiramento é o conjunto responsável por esticar o filme stretch antes de aplicá-lo na carga, garantindo economia de material, maior estabilidade do pallet e padronização do processo de embalagem.",
        detailedDescription: "Ele é um dos componentes mais importantes da máquina envolvedora, pois determina o nível de eficiência e desempenho do sistema. Com sistema de pré estiramento e tecnologia RKtorque, possui estiramento de 100 a 300% do filme, podendo chegar a uma redução no consumo de filme stretch de até 75%.",
        image: "https://customer-assets.emergentagent.com/job_automation-hub-52/artifacts/cdcklfpl_CABECOTE_AUTO.jpeg",
        keywords: "cabeçote automático stretch, sistema automático paletização, RKtorque, pré-estiramento",
        hasDetails: true
      },
      {
        title: "Dispositivo de Processo Contínuo (Pinça e Corte Automático)",
        description: "O sistema de pinça e corte automático é um conjunto eletromecânico integrado às máquinas envolvedoras de filme stretch, responsável por automatizar o início e o término do ciclo de paletização sem intervenção do operador.",
        detailedDescription: "Ele executa três funções principais:\n\n1. Fixação inicial do filme - Antes de iniciar o estiramento, a pinça se fecha e segura a ponta do filme stretch, garantindo que o filme fique preso ao pallet sem que o operador precise amarrar ou posicionar manualmente.\n\n2. Liberação controlada durante a operação - Quando a máquina começa a envolver o pallet, a pinça abre no momento exato, liberando o filme para que a rotação do disco e a tração do estiramento façam o enrolamento automático.\n\n3. Fechamento e corte no final do ciclo - Após finalizar o envolvimento, a pinça volta a se fechar, segurando o filme novamente. Um mecanismo de corte secciona o filme de forma limpa e segura. O sistema mantém a ponta do filme presa à carga, evitando soltura e garantindo acabamento profissional.",
        image: "https://customer-assets.emergentagent.com/job_automation-hub-52/artifacts/oia7c8bb_CORTE.jpeg",
        keywords: "processo contínuo embalagem, automação paletização, linha contínua, pinça automática, corte automático",
        hasDetails: true
      },
      {
        title: "Disco Duplo",
        description: "Sistema de disco duplo: um único cabeçote opera alternadamente entre dois discos, eliminando o tempo de abastecimento da carga e garantindo fluxo contínuo de envolvimento. Uma solução com excelente custo-benefício para operações de alto volume.",
        image: "https://images.pexels.com/photos/34585143/pexels-photo-34585143.jpeg",
        keywords: "disco duplo paletizadora, sistema rotativo, estabilidade de carga, alto volume",
        hasDetails: false,
        hidden: true  // Oculto temporariamente - definir se será vendido ou não
      },
      {
        title: "Pressor de Topo",
        description: "Sistema utilizado para dar estabilidade na carga paletizada, usado principalmente em cargas leves ou que não possuem boa estabilidade ao movimento.",
        image: "https://customer-assets.emergentagent.com/job_automation-hub-52/artifacts/6gawiso0_WhatsApp%20Image%202026-02-27%20at%2014.39.04.jpeg",
        keywords: "pressor de topo, estabilidade de carga, paletização, sistema de pressão",
        hasDetails: false
      }
    ]
  };

  const team = [
    {
      name: "André Garcia",
      role: "Engenheiro Mecânico",
      experience: "16 anos de experiência",
      specialty: "Especialista em máquinas e processos industriais"
    },
    {
      name: "Bruno de Oliveira Ribeiro",
      role: "Tecnólogo em Automação Industrial",
      experience: "13 anos de experiência",
      specialty: "Especialista em integração de sistemas e automação avançada"
    },
    {
      name: "Daniel Pontes de Azeredo",
      role: "Engenheiro de Produção",
      experience: "17 anos de experiência",
      specialty: "Foco em estratégia, negócios e gestão técnica"
    },
    {
      name: "Renan de Oliveira Ribeiro",
      role: "Engenheiro Eletricista",
      experience: "10 anos de experiência",
      specialty: "Especialista em projetos de automação e desenvolvimento de máquinas"
    }
  ];

  const values = [
    "Excelência",
    "Inovação",
    "Paixão",
    "Integridade",
    "Colaboração",
    "Comprometimento com o cliente"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Context - Hidden keywords for search engines */}
      <div className="sr-only" aria-hidden="true">
        <h2>Rainmaker Engenharia e Automação - Empresa de Automação Industrial em Marília SP</h2>
        <p>Somos uma empresa brasileira especializada em soluções completas para indústria, localizada em Marília, São Paulo. Atuamos em todo território nacional oferecendo serviços de automação industrial, desenvolvimento de máquinas especiais customizadas, montagem de painéis elétricos, adequação de normas regulamentadoras NR10 e NR12, manutenção industrial preventiva e corretiva.</p>
        <p>Principais produtos e serviços: strechadeira envolvedora automática para paletização, stretch wrapper industrial, máquinas paletizadoras, envolvedora de filme stretch, cabeçote de pré-estiramento automático, sistemas de embalagem industrial, dispositivos de processo contínuo, pinça e corte automático de filme, projetos de engenharia elétrica e mecânica, integração de sistemas automatizados, retrofit de máquinas industriais, consultoria em indústria 4.0, plano diretor de automação industrial.</p>
        <p>Atendemos os setores: indústria alimentícia, indústria química, indústria farmacêutica, logística e distribuição, armazéns e centros de distribuição, manufatura, metalúrgica, embalagens, papel e celulose.</p>
        <p>Localização: Marília SP, atendimento em todo Brasil incluindo São Paulo, interior paulista, região sudeste, sul e centro-oeste.</p>
      </div>
      
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <a 
            href="#" 
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="text-2xl font-bold text-rainmaker-primary cursor-pointer hover:text-rainmaker-primary/80 transition-colors" 
            translate="no"
          >
            RAINMAKER
          </a>
          <nav className="hidden md:flex gap-6">
            <a href="#sobre" className="text-gray-700 hover:text-rainmaker-primary transition-colors">Sobre</a>
            <a href="#time" className="text-gray-700 hover:text-rainmaker-primary transition-colors">Time</a>
            <a href="#produtos" className="text-gray-700 hover:text-rainmaker-primary transition-colors">Produtos</a>
            <a href="#solucoes" className="text-gray-700 hover:text-rainmaker-primary transition-colors">Soluções</a>
            <a href="#contato" className="text-gray-700 hover:text-rainmaker-primary transition-colors">Contato</a>
          </nav>
          <Button className="bg-rainmaker-primary hover:bg-rainmaker-primary/90">
            <a href="#contato">Fale Conosco</a>
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative flex flex-col mt-16 bg-white" style={{ height: 'calc(100vh - 64px)' }}>
        {/* Título acima de tudo */}
        <div className="container mx-auto px-4 py-4 text-center">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-rainmaker-dark leading-tight">
            Engenharia Inteligente Para Acelerar a Evolução da Indústria Brasileira
          </h1>
        </div>
        
        {/* Grid: Vídeo (esquerda) + Texto/Botões (direita) */}
        <div className="flex-1 grid md:grid-cols-2 gap-0 overflow-hidden">
          {/* Vídeo à esquerda */}
          <div className="relative bg-white">
            <div className="absolute inset-0 bg-gradient-to-r from-rainmaker-dark/70 via-rainmaker-dark/60 to-rainmaker-primary/50 z-10" />
            <video 
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="w-full h-full object-contain"
            >
              <source src="https://customer-assets.emergentagent.com/job_automation-hub-52/artifacts/njji6zhb_WhatsApp%20Video%202026-02-27%20at%2014.39.17.mp4" type="video/mp4" />
              Seu navegador não suporta a reprodução de vídeos.
            </video>
          </div>
          
          {/* Texto e botões à direita */}
          <div className="flex items-center justify-center p-6 bg-white">
            <div className="max-w-xl text-center md:text-left">
              <p className="text-xl md:text-2xl lg:text-3xl mb-6 text-gray-700 leading-relaxed">
                Na <span translate="no">Rainmaker</span>, simplificamos o complexo e entregamos resultados reais, combinando expertise técnica, inovação contínua e compromisso absoluto com a performance
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Button size="lg" className="bg-rainmaker-primary text-white hover:bg-rainmaker-primary/90 px-8 py-3 text-lg">
                  <a href="#contato">Solicite um Orçamento</a>
                </Button>
                <Button size="lg" variant="outline" className="border-rainmaker-primary text-rainmaker-primary hover:bg-rainmaker-primary/10 px-8 py-3 text-lg">
                  <a href="#contato">Fale com um Especialista</a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quem Somos */}
      <section id="sobre" className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-rainmaker-dark">
              Nossa História é Escrita com Engenharia, Paixão e Propósito
            </h2>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p>
                A <span translate="no">Rainmaker</span> nasceu para fortalecer a engenharia brasileira e elevar sua autonomia tecnológica. Criada por especialistas com ampla experiência, reunimos competências multidisciplinares em uma estrutura integrada, preparada para resolver desafios com eficiência e entregar resultados consistentes, sem complexidade desnecessária.
              </p>
              <p>
                Atuamos com excelência em projetos de automação industrial e fabricação de máquinas especiais, desenvolvendo soluções completas que reduzem a dependência de fornecedores internacionais e elevam o padrão da engenharia nacional.
              </p>
              <p className="font-semibold text-rainmaker-primary">
                Hoje, somos referência em projetos de alta complexidade, impulsionando a inovação e a competitividade da indústria brasileira.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Missão, Visão e Valores */}
      <section className="py-24 bg-rainmaker-primary text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-12 max-w-6xl mx-auto">
            <div className="text-center">
              <h3 className="text-3xl font-bold mb-4">Missão</h3>
              <p className="text-lg opacity-95">
                Criar e entregar soluções inovadoras em engenharia industrial, assegurando eficiência, desempenho e alta produtividade.
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-3xl font-bold mb-4">Visão</h3>
              <p className="text-lg opacity-95">
                Ser referência em engenharia industrial no Brasil, reconhecida pela excelência e pela integração multidisciplinar.
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-3xl font-bold mb-4">Valores</h3>
              <div className="flex flex-wrap justify-center gap-2">
                {values.map((value, index) => (
                  <span key={index} className="bg-white/20 px-3 py-1 rounded-full text-sm">
                    {value}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nosso Time */}
      <section id="time" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-rainmaker-dark">
              Uma equipe que transforma desafios em resultados
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Por trás da <span translate="no">Rainmaker</span>, há engenheiros e especialistas com sólida experiência, visão estratégica e paixão pela engenharia.
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <p className="text-center text-2xl md:text-3xl leading-relaxed text-gray-700 font-medium">
              Com <span className="font-bold text-rainmaker-primary">mais de 56 anos de engenharia acumulada</span>, unimos mecânica, elétrica, automação e processos em uma força multidisciplinar capaz de transformar desafios em resultados reais, entregando soluções industriais robustas, precisas e orientadas à máxima performance, elevando o desempenho da sua indústria ao mais alto nível.
            </p>
          </div>
        </div>
      </section>

      {/* Parcerias */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-rainmaker-dark">
              Grandes Resultados Nascem de Grandes Parcerias
            </h2>
            <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
              <p>
                Trabalhamos com empresas consolidadas que compartilham nossos valores: qualidade, comprometimento e excelência técnica.
              </p>
              <p className="font-semibold text-rainmaker-primary text-xl">
                Na <span translate="no">Rainmaker</span>, parceria significa confiança, propósito e compromisso compartilhado com o sucesso de cada cliente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Produtos: Máquinas e Equipamentos Industriais */}
      <section id="produtos" className="py-24 bg-rainmaker-dark text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 scroll-mt-20 pt-4">
              Máquinas e Equipamentos Industriais
            </h2>
          </div>
          
          {/* Produto Principal */}
          <div className="max-w-6xl mx-auto mb-12">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 overflow-hidden">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="relative h-80 md:h-auto">
                  <img 
                    src={machines.image} 
                    alt="Strechadeira Envolvedora de Filme Stretch Industrial - Paletizadora Automática Stretch Wrapper - Rainmaker"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 flex flex-col justify-center">
                  <CardTitle className="text-2xl md:text-3xl text-white mb-4">
                    {machines.title}
                  </CardTitle>
                  <p className="text-white/90 text-lg leading-relaxed mb-4">
                    {machines.description}
                  </p>
                  {/* Keywords ocultas visualmente, mas presentes no HTML para SEO */}
                  <div className="sr-only" aria-hidden="true">
                    {machines.keywords.split(', ').map((keyword, idx) => (
                      <span key={idx}>
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Opcionais */}
          <div className="max-w-6xl mx-auto">
            <h4 className="text-2xl font-bold text-center mb-8 text-white/90">
              Opcionais Disponíveis
            </h4>
            <div className="flex flex-wrap justify-center gap-6">
              {machines.optionals.filter(opt => !opt.hidden).map((optional, index) => (
                <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all w-full md:w-[calc(50%-12px)] lg:w-[calc(25%-18px)] max-w-[280px]">
                  <div className="relative h-48 overflow-hidden bg-white/5">
                    <img 
                      src={optional.image} 
                      alt={`${optional.title} - ${optional.keywords} - Opcional para Strechadeira Rainmaker`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-lg text-white">{optional.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white/90 text-sm mb-3">{optional.description}</p>
                    {optional.hasDetails && (
                      <>
                        {expandedOptionals[index] && (
                          <p className="text-white/90 text-sm mb-3 mt-3 border-t border-white/20 pt-3">
                            {optional.detailedDescription}
                          </p>
                        )}
                        <button
                          ref={el => optionalRefs.current[index] = el}
                          onClick={() => toggleOptionalDetails(index)}
                          className="text-white text-sm font-semibold hover:text-white/80 transition-colors flex items-center gap-1"
                        >
                          {expandedOptionals[index] ? 'Ver Menos' : 'Saiba Mais'}
                          <ChevronRight className={`w-4 h-4 transition-transform ${expandedOptionals[index] ? 'rotate-90' : ''}`} />
                        </button>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Soluções: Serviços */}
      <section id="solucoes" className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-rainmaker-dark">
              Soluções Integradas para Elevar o Padrão da Engenharia Industrial
            </h2>
          </div>

          {/* Serviços */}
          <div>
            <h3 className="text-3xl md:text-4xl font-bold text-center mb-12 text-rainmaker-dark">
              Serviços de Engenharia e Automação Industrial
            </h3>
            
            {/* Serviço em Destaque */}
            <div className="max-w-6xl mx-auto mb-12">
              <Card className="border-2 border-rainmaker-primary/30 shadow-xl overflow-hidden">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="relative h-80">
                    <img 
                      src={featuredService.image} 
                      alt="Desenvolvimento de Máquinas e Equipamentos Industriais Customizados - Engenharia Mecânica - Fabricação Sob Medida - Rainmaker"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 flex flex-col justify-center">
                    <CardTitle className="text-2xl md:text-3xl text-rainmaker-dark mb-4">
                      {featuredService.title}
                    </CardTitle>
                    <p className="text-gray-700 text-lg leading-relaxed mb-4">
                      {featuredService.description}
                    </p>
                    {expandedFeaturedService && (
                      <p className="text-gray-700 text-lg leading-relaxed mb-4 mt-3 border-t border-gray-200 pt-3">
                        {featuredService.detailedDescription}
                      </p>
                    )}
                    <button
                      ref={featuredServiceRef}
                      onClick={toggleFeaturedServiceDetails}
                      className="text-rainmaker-primary text-sm font-semibold hover:text-rainmaker-primary/80 transition-colors flex items-center gap-1 mb-4"
                    >
                      {expandedFeaturedService ? 'Ver Menos' : 'Saiba Mais'}
                      <ChevronRight className={`w-4 h-4 transition-transform ${expandedFeaturedService ? 'rotate-90' : ''}`} />
                    </button>
                    {/* Keywords ocultas visualmente, mas presentes no HTML para SEO */}
                    <div className="sr-only" aria-hidden="true">
                      {featuredService.keywords.split(', ').map((keyword, idx) => (
                        <span key={idx}>
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Demais Serviços */}
            <div className="flex flex-wrap justify-center gap-8">
              {services.map((service, index) => (
                <Card key={index} className="border-2 hover:border-rainmaker-primary transition-all hover:shadow-lg w-full md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]">
                  <CardHeader>
                    <div className="text-rainmaker-primary mb-4">{service.icon}</div>
                    <CardTitle className="text-xl text-rainmaker-dark">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contato */}
      <section id="contato" className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-rainmaker-dark">
                Fale com a <span translate="no">Rainmaker</span> e Transforme Seu Projeto em Realidade
              </h2>
              <p className="text-xl text-gray-600">
                Entre em contato e descubra como podemos elevar o nível da sua operação industrial.
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Formulário */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Envie sua Mensagem</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Nome</label>
                      <Input 
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Seu nome completo"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">E-mail</label>
                      <Input 
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="seu@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Empresa</label>
                      <Input 
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                        placeholder="Nome da sua empresa"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Mensagem</label>
                      <Textarea 
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        placeholder="Como podemos ajudar?"
                        rows={5}
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-rainmaker-primary hover:bg-rainmaker-primary/90"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Enviando...' : 'Enviar Mensagem'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Informações de Contato */}
              <div className="space-y-6">
                <Card>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-rainmaker-primary mt-1" />
                        <div>
                          <p className="font-semibold">Agendamentos e Orçamentos</p>
                          <a 
                            href="https://wa.me/5519999200077" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-rainmaker-primary hover:text-rainmaker-primary/80 hover:underline transition-colors font-medium"
                          >
                            +55 19 99920-0077
                          </a>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Zap className="w-5 h-5 text-rainmaker-primary mt-1" />
                        <div>
                          <p className="font-semibold">Assistência 24h</p>
                          <a 
                            href="https://wa.me/5514998808318" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-rainmaker-primary hover:text-rainmaker-primary/80 hover:underline transition-colors font-medium"
                          >
                            +55 14 99880-8318
                          </a>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-rainmaker-primary mt-1" />
                        <div>
                          <p className="font-semibold">E-mail</p>
                          <p className="text-gray-600">contato@rainmakerautomacao.com.br</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-rainmaker-primary mt-1" />
                        <div>
                          <p className="font-semibold">Endereço</p>
                          <p className="text-gray-600">Rua Almirante Tamandaré, nº 520</p>
                          <p className="text-gray-600">Bairro Alto Cafezal</p>
                          <p className="text-gray-600">CEP: 17504-070 — Marília/SP</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-rainmaker-primary mt-1" />
                        <div>
                          <p className="font-semibold">Horário de Atendimento</p>
                          <p className="text-gray-600">Comercial estendido: 6h às 20h</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Google Maps */}
                <div className="h-80 rounded-lg overflow-hidden shadow-lg">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3682.9997826441654!2d-49.94633!3d-22.21471!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94bfd1e3c03fffff%3A0x5555!2sRua%20Almirante%20Tamandar%C3%A9%2C%20520%20-%20Alto%20Cafezal%2C%20Mar%C3%ADlia%20-%20SP%2C%2017504-070!5e0!3m2!1spt-BR!2sbr!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Localização Rainmaker"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-rainmaker-dark text-white py-12">
        <div className="container mx-auto px-4">
          {/* SEO Footer Context - Hidden keywords */}
          <div className="sr-only" aria-hidden="true">
            <h3>Empresa de Engenharia em Marília SP - Contato e Localização</h3>
            <p>Rainmaker Engenharia e Automação está localizada em Marília, São Paulo. Atendemos empresas em todo Brasil com soluções em automação industrial. Entre em contato pelo email contato@rainmakerautomacao.com.br</p>
            <p>Especialidades técnicas: CLP (Controlador Lógico Programável), IHM (Interface Homem Máquina), supervisório SCADA, inversores de frequência, servo motores, robótica industrial, sensoriamento industrial, redes industriais Profibus Profinet Ethernet IP, integração com ERP MES, rastreabilidade industrial, OEE, TPM, Lean Manufacturing</p>
            <p>Certificações e normas: NR-10 (Segurança em Instalações Elétricas), NR-12 (Segurança no Trabalho em Máquinas e Equipamentos), NR-33, NR-35, ISO 9001, certificação de painéis elétricos conforme normas ABNT NBR IEC</p>
          </div>
          
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4" translate="no">RAINMAKER</h3>
            <p className="text-lg mb-2">Engenharia e Automação</p>
            <p className="text-white/80 max-w-2xl mx-auto mb-6">
              Transformamos complexidade em resultado. Engenharia nacional feita com precisão, propósito e paixão.
            </p>
            
            {/* Redes Sociais */}
            <div className="flex justify-center gap-6 mb-6">
              <a 
                href="https://www.instagram.com/rainmakerautomacao/?igsh=NHF4OGF5OHBuaHJ6#" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-rainmaker-primary transition-colors transform hover:scale-110"
                aria-label="Instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
              <a 
                href="https://www.linkedin.com/company/rainmaker-engenharia-e-automa%C3%A7%C3%A3o/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white hover:text-rainmaker-primary transition-colors transform hover:scale-110"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-6 h-6" />
              </a>
            </div>
            
            <div className="border-t border-white/20 pt-6 mt-6">
              <p className="text-white/60">
                © 2025 <span translate="no">Rainmaker</span> Engenharia e Automação. Todos os direitos reservados.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;